import React from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Link as RouterLink,
} from "react-router-dom";
import { Link as ScrollLink } from "react-scroll";
import { ReactTyped } from "react-typed";
import { motion } from "framer-motion";
import "./App.css";

/* ---------------- Privacy Policy page ---------------- */

const PrivacyPolicy = () => {
  return (
    <div className="section legal">
      <div className="legal-container">
        <h1 className="legal-heading">Privacy Policy — Analytics Harvest</h1>
        <p>
          <em>Last updated: September 2026</em>
        </p>

        <p>
          Analytics Harvest (“we”, “us”, “our”) operates this website and may
          access limited information in order to provide Instagram content
          automation features for our own managed accounts.
        </p>

        <p>
          We do not collect, store, share, or sell personal data of users. No
          sensitive information is processed. 
        </p>

        <p>
          If you have questions, contact us at{" "}
          <a href="mailto:info@analyticsharvest.com">
            info@analyticsharvest.com
          </a>
          .
        </p>

        <RouterLink to="/" className="back-link">
          ← Back to main page
        </RouterLink>
      </div>
    </div>
  );
};

/* ---------------- Main one-page site ---------------- */

const MainPage = () => {
  return (
    <div>
      {/* Navigation Bar */}
      <nav className="navbar">
        <ul>
          <li>
            <ScrollLink to="home" smooth={true} duration={500}>
              Home
            </ScrollLink>
          </li>
          <li>
            <ScrollLink to="about" smooth={true} duration={500}>
              About
            </ScrollLink>
          </li>
          <li>
            <ScrollLink to="services" smooth={true} duration={500}>
              Services
            </ScrollLink>
          </li>
          <li>
            <ScrollLink to="contact" smooth={true} duration={500}>
              Contact
            </ScrollLink>
          </li>
        </ul>
      </nav>

      {/* Home Section */}
      <div className="section home" id="home">
        <div className="home-content">
          <motion.h1
            className="home-title"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 2, ease: "easeInOut" }}
          >
            <ReactTyped
              strings={[
                "DATA INSIGHTS CONSULTANT",
                "BUSINESS INTELLIGENCE ANALYST",
                "UNLOCKING THE POWER OF DATA",
              ]}
              typeSpeed={80}
              backSpeed={40}
              backDelay={2000}
              loop
            />
          </motion.h1>

          <motion.p
            className="home-subtitle"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 2, delay: 0.5, ease: "easeInOut" }}
          >
            Transforming Data into Decisions
          </motion.p>

          <motion.p
            className="home-description"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 2, delay: 1, ease: "easeInOut" }}
          >
            Hi, I’m <strong>Guoda</strong>, a Data Scientist and Business
            Intelligence Analyst based in Vilnius, Lithuania. With skills in{" "}
            <strong>SQL, Python, and data visualization</strong>, I turn complex
            data into clear insights that support better business decisions.
          </motion.p>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 2, delay: 1.5 }}
          >
            <ScrollLink
              to="about"
              smooth={true}
              duration={500}
              className="home-button"
            >
              Learn More
            </ScrollLink>
          </motion.div>
        </div>
      </div>

      {/* About Section */}
      <div className="section about" id="about">
        <div className="about-container">
          <h1 className="about-heading">About Me</h1>
          <p className="about-text">
            I’m <strong>Guoda</strong>, a Data Scientist and Business Intelligence
            Analyst based in Vilnius, Lithuania. I use{" "}
            <strong>SQL, Python, and data visualization tools</strong> to help
            organizations understand their data and make informed decisions.
          </p>
          <p className="about-text">
            My expertise spans{" "}
            <strong>
              dashboard development, ETL optimization, predictive modeling, and
              business intelligence
            </strong>
            . I enjoy turning complex information into practical insights,
            whether that means identifying trends, improving reporting, or
            making data easier to access and use.
          </p>
          <p className="about-text">
            I’m curious by nature and always keen to learn, solve new problems,
            and explore how analytics can address real business challenges.
          </p>
        </div>
      </div>

      {/* Services Section */}
      <div className="section services" id="services">
        <div className="services-container">
          <motion.div
            className="services-header"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: "easeOut" }}
          >
            <h2 className="services-title">DATA SOLUTIONS </h2>
            <h3 className="services-subtitle">Transforming insights into action</h3>
          </motion.div>

          <div className="services-grid">
            <motion.div
              className="service-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.2 }}
            >
              <div className="service-text">
                <h4>
                  Data Visualization <span>›</span>
                </h4>
                <p>Transform complex data into compelling visual stories.</p>
              </div>
            </motion.div>

            <motion.div
              className="service-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.4 }}
            >
              <div className="service-text">
                <h4>
                  Predictive Modeling <span>›</span>
                </h4>
                <p>Leverage historical data to forecast future outcomes effectively.</p>
              </div>
            </motion.div>

            <motion.div
              className="service-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.6 }}
            >
              <div className="service-text">
                <h4>
                  ETL Pipeline Optimization <span>›</span>
                </h4>
                <p>Streamline your data processing for enhanced efficiency.</p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Contact Section */}
      <div className="section contact" id="contact">
        <div className="contact-container">
          <motion.h1
            className="contact-title"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.5, ease: "easeOut" }}
          >
            Contact
          </motion.h1>

          <motion.div
            className="contact-info-box"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 2, delay: 0.5 }}
          >
            <p>
              📧 <strong>Email:</strong>{" "}
              <a href="mailto:info@analyticsharvest.com">
                info@analyticsharvest.com
              </a>
            </p>
            <p>
              🔗 <strong>LinkedIn:</strong>{" "}
              <a
                href="https://www.linkedin.com/in/guodab/"
                target="_blank"
                rel="noopener noreferrer"
              >
                LinkedIn Profile
              </a>
            </p>
          </motion.div>
        </div>
      </div>

      {/* Footer */}
      <footer className="site-footer">
        <div className="footer-content">
          <span>
            © 2026 Analytics Harvest, Vilnius, Lithuania
          </span>
          <RouterLink to="/privacy-policy" className="legal-link">
            Privacy Policy
          </RouterLink>
        </div>
      </footer>
    </div>
  );
};

/* ---------------- Router wrapper ---------------- */

const App = () => {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<MainPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      </Routes>
    </BrowserRouter>
  );
};

export default App;